import { memo, useDeferredValue, useMemo, useState } from 'react';
import {
  Controller,
  ControllerProps,
  FieldValues,
  Path,
} from 'react-hook-form';

import {
  DisbursementProvider,
  useDisbursementProviders,
} from '~domains/partner-application/entities/disbursement-providers';

import { Combobox } from '@breezeventures/ui-kit/combobox/rhf';
import { Typography } from '@breezeventures/ui-kit/typography';
import cn from 'classnames';

type SelectDisbursementProviderProps<T extends FieldValues> = Omit<
  ControllerProps<T>,
  'render' | 'name'
> & {
  name: Path<T>;
  onChange?: (providerId: string) => void;
};

export const SelectDisbursementProvider = <T extends FieldValues>({
  name,
  onChange,
  ...controllerProps
}: SelectDisbursementProviderProps<T>) => {
  const { isLoading, data: providers = [] } = useDisbursementProviders();

  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery);

  const providerNamesMap = useMemo(
    () => Object.fromEntries(providers.map((p) => [p.id, p.brand.name])),
    [providers]
  );

  return (
    <Controller
      {...controllerProps}
      name={name}
      render={(controllerRenderProps) => {
        const { field, ...controllerRest } = controllerRenderProps;
        return (
          <Combobox
            {...controllerRest}
            field={{
              ...field,
              onChange: (value: string) => {
                field.onChange(value);
                setSearchQuery('');
                onChange?.(value);
              },
            }}
            onClose={() => setSearchQuery('')}
            disabled={isLoading}
            textFieldProps={{
              label: 'Bank',
              labelPosition: 'inner',
              onChange: (e) => setSearchQuery(e.target.value),
              displayValue: (id) => {
                if (searchQuery) return searchQuery;
                return id ? providerNamesMap[id] || '' : '';
              },
            }}
          >
            <SelectDisbursementProviderOptions
              providers={providers}
              searchQuery={deferredSearchQuery}
              isLoading={isLoading}
            />
          </Combobox>
        );
      }}
    />
  );
};

const SelectDisbursementProviderOptions = memo(
  function SelectDisbursementProviderOptions({
    providers,
    searchQuery,
    isLoading,
  }: {
    providers: DisbursementProvider[];
    searchQuery: string;
    isLoading: boolean;
  }) {
    const filteredProviders = useMemo(() => {
      if (!searchQuery.trim()) return providers;
      const query = searchQuery.trim().toLowerCase();
      return providers.filter((p) =>
        p.brand.name.toLowerCase().includes(query)
      );
    }, [providers, searchQuery]);

    if (isLoading) {
      return (
        <Combobox.Option value="" disabled>
          <Typography.Text size="m" color="secondary">
            Loading...
          </Typography.Text>
        </Combobox.Option>
      );
    }

    if (filteredProviders.length === 0) {
      return (
        <Combobox.Option value="" disabled>
          <Typography.Text size="m" color="secondary">
            No providers found
          </Typography.Text>
        </Combobox.Option>
      );
    }

    return filteredProviders.map((provider) => {
      const isDisabled = provider.status !== 'ENABLED';

      return (
        <Combobox.Option
          key={provider.id}
          value={provider.id}
          disabled={isDisabled}
        >
          <div className="flex items-center gap-2">
            {provider.brand.logoUrl && (
              <img
                src={provider.brand.logoUrl}
                alt={provider.brand.name}
                className={cn('size-8', { 'opacity-50': isDisabled })}
              />
            )}
            <div>
              <Typography.Text
                size="m"
                color={isDisabled ? 'secondary' : 'primary'}
              >
                {provider.brand.name}
              </Typography.Text>
              {isDisabled && (
                <Typography.Text size="xs" color="tertiary">
                  Temporary unavailable
                </Typography.Text>
              )}
            </div>
          </div>
        </Combobox.Option>
      );
    });
  }
);
